import { createTheme, responsiveFontSizes } from '@mui/material/styles';

const bodyFontFamily = 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const headingFontFamily = '"Plus Jakarta Sans", Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const monoFontFamily = 'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace';

const modernColors = {
  gradient: {
    primary: 'linear-gradient(135deg, #086d86 0%, #1f6f8b 45%, #349682 100%)',
    secondary: 'linear-gradient(135deg, #1f6f8b 0%, #2ca58d 100%)',
    accent: 'linear-gradient(135deg, #bb8b57 0%, #d5ad81 100%)',
    dark: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    background:
      'radial-gradient(circle at 18% 12%, rgba(44,165,141,0.16) 0%, rgba(44,165,141,0) 32%), radial-gradient(circle at 82% 10%, rgba(31,111,139,0.14) 0%, rgba(31,111,139,0) 38%), linear-gradient(180deg, #f7fafc 0%, #edf3f5 50%, #f8fcfb 100%)',
    luxury: 'linear-gradient(135deg, #8b5e34 0%, #be8f64 100%)',
    nature: 'linear-gradient(135deg, #1c7a64 0%, #2ca58d 100%)',
    primary_reverse: 'linear-gradient(135deg, #2ca58d 0%, #1f6f8b 55%, #0f4c5c 100%)',
    white: 'linear-gradient(145deg, #ffffff 0%, #f7fbfd 100%)',
    page:
      'radial-gradient(circle at 10% 12%, rgba(44,165,141,0.12) 0%, rgba(44,165,141,0) 32%), radial-gradient(circle at 88% 8%, rgba(31,111,139,0.12) 0%, rgba(31,111,139,0) 36%), linear-gradient(180deg, #f6fbfd 0%, #ecf3f6 55%, #f7fbfa 100%)',
  },
  modern: {
    slate: {
      50: '#f6f9fb',
      100: '#edf2f7',
      200: '#d8e1ea',
      300: '#bccad8',
      400: '#8ea1b2',
      500: '#6d8294',
      600: '#526779',
      700: '#3e5263',
      800: '#2b3f50',
      900: '#182c3d',
    },
    mainColor: {
      50: '#eefaf8',
      100: '#d8f2ed',
      200: '#bae8de',
      300: '#8fd8ca',
      400: '#63c5b4',
      500: '#2ca58d',
      600: '#208673',
      700: '#1a6b5d',
      800: '#16554b',
      900: '#13453d',
      950: '#0c2a25',
    },
    pink: {
      50: '#fcf5f2',
      100: '#f8e5dd',
      200: '#f1cdbf',
      300: '#e8af98',
      400: '#db8f73',
      500: '#c87862',
      600: '#a85f4c',
      700: '#87493a',
      800: '#65362b',
      900: '#45241d',
    },
    blue: {
      50: '#eff7fb',
      100: '#dceef7',
      200: '#c0dfed',
      300: '#95c8dc',
      400: '#6faec7',
      500: '#3f89a9',
      600: '#2f6f8d',
      700: '#245a73',
      800: '#1a4357',
      900: '#132f3e',
    },
  },
};

const modernTypography = {
  gradientText: {
    background: modernColors.gradient.primary,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.02em',
  },
  modernHeading: {
    fontFamily: headingFontFamily,
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color: modernColors.modern.slate[900],
  },
  modernSubtext: {
    color: modernColors.modern.slate[600],
    lineHeight: 1.7,
    letterSpacing: '0.01em',
  },
};

const responsiveSizing = {
  controls: {
    inputMinHeight: { xs: 44, sm: 40 },
    inputFontSize: { xs: '0.95rem', sm: '0.875rem' },
    inputLabelFontSize: { xs: '0.85rem', sm: '0.8rem' },
    buttonMinHeight: { xs: 44, sm: 40 },
    buttonPadding: { xs: '10px 14px', sm: '10px 18px' },
    buttonFontSize: { xs: '0.85rem', sm: '0.9rem' },
    iconButtonSize: { xs: 38, sm: 36 },
    iconSize: { xs: '18px', sm: '20px' },
    iconSizeSmall: { xs: '16px', sm: '18px' },
    iconSizeLarge: { xs: '24px', sm: '28px' },
    chipHeight: { xs: 24, sm: 28 },
    chipLabelFontSize: { xs: '0.72rem', sm: '0.8rem' },
    tabMinHeight: { xs: 42, sm: 48 },
    tabFontSize: { xs: '0.76rem', sm: '0.86rem' },
    tabIconSize: { xs: '1rem', sm: '1.2rem' },
  },
  dialog: {
    paperRadius: { xs: '0px', sm: '16px' },
    paperMargin: { xs: '0px', sm: '16px' },
    paperWidth: { xs: '100vw', sm: 'calc(100% - 64px)' },
    paperMaxWidth: { xs: '100vw', sm: 'calc(100% - 64px)' },
    contentHeight: { xs: '100dvh', sm: '97vh' },
    headerPadding: { xs: 0.75, sm: 2 },
    contentPaddingX: { xs: 0.75, sm: 2, md: 3 },
    contentNegativeMarginX: { xs: -0.75, sm: -2, md: -3 },
    contentPaddingBottom: { xs: 1.5, sm: 3 },
    titlePadding: { xs: '10px 12px', sm: '16px 24px' },
    bodyPadding: { xs: '8px 12px', sm: '12px 24px' },
    actionsPadding: { xs: '8px 12px', sm: '8px 16px' },
  },
  chatPanel: {
    floatingFabSize: { xs: 58, sm: 56 },
    floatingFabIconSize: { xs: 26, sm: 24 },
    panelTop: { xs: 220, sm: 70 },
    panelBottom: { xs: 1, sm: 6 },
    panelHorizontalInset: { xs: 4, sm: 0 },
    panelWidth: { xs: 'calc(100vw - 10px)', sm: '100%' },
    panelMaxWidth: { xs: 'calc(100vw - 10px)', sm: '100%' },
    panelShadow: { xs: '0 0 24px rgba(0,0,0,0.18)', sm: '2px 0 8px rgba(0,0,0,0.1)' },
    panelRadius: { xs: '16px', sm: '0 20px 20px 0' },
    headerPadding: { xs: 1, sm: 0.5 },
    headerBorderBottom: { xs: '1px solid #f1f1f1', sm: '0px solid #f1f1f1' },
    headerMinHeight: { xs: 52, sm: 39 },
    headerActionsGap: { xs: 1, sm: 0.5 },
    actionButtonSize: { xs: 40, sm: 32 },
    actionIconSize: { xs: 22, sm: 18 },
    minimizedActionButtonSize: { xs: 40, sm: 28 },
    minimizedActionIconSize: { xs: 22, sm: 16 },
  },
  propertyCard: {
    imageHeight: { xs: 220, sm: 260 },
    navButtonSize: { xs: 34, sm: 30 },
    navIconSize: { xs: 22, sm: 20 },
    watchlistIconSize: { xs: 28, sm: 26 },
    badgePadding: { xs: '3px 7px', sm: '4px 8px' },
    badgeFontSize: { xs: '0.7rem', sm: '0.75rem' },
    tagPadding: { xs: '3px 10px', sm: '4px 12px' },
    tagFontSize: { xs: '0.75rem', sm: '0.875rem' },
  },
};

const themeShadows = [
  'none',
  ...Array.from({ length: 24 }, (_, index) => {
    const y = Math.round(2 + index * 1.9);
    const blur = Math.round(6 + index * 2.7);
    const alpha = Math.min(0.05 + index * 0.009, 0.26).toFixed(3);
    return `0 ${y}px ${blur}px rgba(17, 59, 78, ${alpha})`;
  }),
];

let theme = createTheme({
  palette: {
    primary: {
      main: '#1f6f8b',
      light: '#3f89a9',
      dark: '#16556b',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#2ca58d',
      light: '#63c5b4',
      dark: '#1a6b5d',
      contrastText: '#ffffff',
    },
    background: {
      default: '#eff4f6',
      paper: '#ffffff',
    },
    text: {
      primary: modernColors.modern.slate[900],
      secondary: modernColors.modern.slate[600],
    },
    error: {
      main: '#b54444',
      light: '#d96f6f',
      dark: '#842f2f',
    },
    warning: {
      main: '#b8854b',
      light: '#d5ad81',
      dark: '#8d6436',
    },
    info: {
      main: '#2f6f8d',
      light: '#6faec7',
      dark: '#1a4357',
    },
    success: {
      main: '#2ca58d',
      light: '#8fd8ca',
      dark: '#1a6b5d',
    },
    modern: modernColors.modern,
  },
  typography: {
    fontFamily: bodyFontFamily,
    h1: {
      fontFamily: headingFontFamily,
      fontWeight: 800,
      letterSpacing: '-0.03em',
      color: modernColors.modern.slate[900],
    },
    h2: {
      fontFamily: headingFontFamily,
      fontWeight: 800,
      letterSpacing: '-0.025em',
      color: modernColors.modern.slate[900],
    },
    h3: {
      fontFamily: headingFontFamily,
      fontWeight: 700,
      letterSpacing: '-0.02em',
      color: modernColors.modern.slate[900],
    },
    h4: {
      fontFamily: headingFontFamily,
      fontWeight: 700,
      letterSpacing: '-0.015em',
      color: modernColors.modern.slate[900],
    },
    h5: {
      fontFamily: headingFontFamily,
      fontWeight: 700,
      color: modernColors.modern.slate[900],
    },
    h6: {
      fontFamily: headingFontFamily,
      fontWeight: 600,
      color: modernColors.modern.slate[800],
    },
    h7: {
      fontFamily: headingFontFamily,
      fontWeight: 600,
      color: modernColors.modern.slate[800],
    },
    h7: {
      fontFamily: headingFontFamily,
      fontWeight: 600,
      color: modernColors.modern.slate[800],
    },
    subtitle1: {
      fontWeight: 600,
      color: modernColors.modern.slate[800],
    },
    subtitle2: {
      fontWeight: 600,
      color: modernColors.modern.slate[700],
    },
    body1: {
      color: modernColors.modern.slate[800],
      lineHeight: 1.65,
    },
    body2: {
      color: modernColors.modern.slate[600],
      lineHeight: 1.55,
    },
    button: {
      fontFamily: headingFontFamily,
      fontWeight: 600,
      letterSpacing: '0.01em',
      textTransform: 'none',
    },
  },
  components: {
    // Global element styles. CssBaseline (App.js) already sets body font, text colour,
    // box-sizing and font smoothing from this theme; index.css keeps only Google Maps overrides.
    MuiCssBaseline: {
      styleOverrides: {
        'html, body, #root': {
          minHeight: '100%',
        },
        body: {
          background: modernColors.gradient.page,
        },
        'h1, h2, h3, h4, h5, h6': {
          fontFamily: headingFontFamily,
        },
        'code, kbd, pre, samp': {
          fontFamily: monoFontFamily,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          textTransform: 'none',
          fontWeight: 600,
          minHeight: responsiveSizing.controls.buttonMinHeight,
          padding: responsiveSizing.controls.buttonPadding,
          fontSize: responsiveSizing.controls.buttonFontSize,
          '& .MuiButton-startIcon .MuiSvgIcon-root, & .MuiButton-endIcon .MuiSvgIcon-root': {
            fontSize: responsiveSizing.controls.iconSize,
          },
        },
        contained: {
          boxShadow: '0 8px 12px rgba(31, 111, 139, 0.18)',
          '&:hover': {
            boxShadow: '0 12px 28px rgba(31, 111, 139, 0.22)',
          },
        },
        outlined: {
          borderWidth: 1.4,
          '&:hover': {
            borderWidth: 1.4,
          },
        },
      },
      variants: [
        {
          props: { variant: 'gradient' },
          style: {
            background: modernColors.gradient.primary,
            color: '#ffffff',
            borderRadius: '999px',
            fontWeight: 700,
            padding: '10px 24px',
            boxShadow: '0 2px 2px rgba(31, 111, 139, 0.24)',
            transition: 'all 0.25s ease',
            '&:hover': {
              background: modernColors.gradient.primary_reverse,
              boxShadow: '0 14px 34px rgba(32, 134, 115, 0.26)',
              transform: 'translateY(-1px)',
            },
          },
        },
        {
          props: { variant: 'gradientWithHover' },
          style: {
            background: modernColors.gradient.primary,
            color: '#ffffff',
            borderRadius: '999px',
            fontWeight: 700,
            padding: '12px 30px',
            boxShadow: '0 12px 10px rgba(31, 111, 139, 0.24)',
            transition: 'all 0.25s ease',
            '&:hover': {
              background: modernColors.gradient.secondary,
              transform: 'translateY(-2px)',
              boxShadow: '0 16px 34px rgba(32, 134, 115, 0.28)',
            },
          },
        },
        {
          props: { variant: 'dark' },
          style: {
            background: modernColors.gradient.dark,
            color: '#ffffff',
            borderRadius: '999px',
            fontWeight: 700,
            padding: '10px 24px',
            boxShadow: '0 2px 2px rgba(15, 23, 42, 0.26)',
            transition: 'all 0.25s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #0c1425 0%, #1a2740 100%)',
              transform: 'translateY(-1px)',
            },
          },
        },
        {
          props: { variant: 'luxury' },
          style: {
            background: modernColors.gradient.luxury,
            color: '#ffffff',
            borderRadius: '999px',
            fontWeight: 700,
            padding: '10px 24px',
            boxShadow: '0 10px 24px rgba(139, 94, 52, 0.3)',
            transition: 'all 0.25s ease',
            '&:hover': {
              background: modernColors.gradient.accent,
              transform: 'translateY(-1px)',
            },
          },
        },
        {
          props: { variant: 'nature' },
          style: {
            background: modernColors.gradient.nature,
            color: '#ffffff',
            borderRadius: '999px',
            fontWeight: 700,
            padding: '10px 24px',
            boxShadow: '0 10px 24px rgba(44, 165, 141, 0.28)',
            transition: 'all 0.25s ease',
            '&:hover': {
              background: modernColors.gradient.secondary,
              transform: 'translateY(-1px)',
            },
          },
        },
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          width: responsiveSizing.controls.iconButtonSize,
          height: responsiveSizing.controls.iconButtonSize,
          padding: { xs: 6, sm: 8 },
          '& .MuiSvgIcon-root': {
            fontSize: responsiveSizing.controls.iconSize,
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: responsiveSizing.controls.iconSize,
        },
        fontSizeSmall: {
          fontSize: responsiveSizing.controls.iconSizeSmall,
        },
        fontSizeLarge: {
          fontSize: responsiveSizing.controls.iconSizeLarge,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: responsiveSizing.controls.tabMinHeight,
          fontSize: responsiveSizing.controls.tabFontSize,
          '& .MuiSvgIcon-root': {
            fontSize: responsiveSizing.controls.tabIconSize,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: responsiveSizing.dialog.paperRadius.sm,
          margin: responsiveSizing.dialog.paperMargin.sm,
          maxHeight: 'calc(100% - 64px)',
          [theme.breakpoints.down('sm')]: {
            borderRadius: responsiveSizing.dialog.paperRadius.xs,
            margin: responsiveSizing.dialog.paperMargin.xs,
            width: responsiveSizing.dialog.paperWidth.xs,
            maxWidth: responsiveSizing.dialog.paperMaxWidth.xs,
            maxHeight: responsiveSizing.dialog.contentHeight.xs,
          },
        }),
        // MUI applies paperFullWidth AFTER paperWidth{Size}, so setting maxWidth
        // here would silently override the dialog's own `maxWidth` prop and make
        // every fullWidth dialog span the viewport. Only the width is set above
        // `sm`; the prop still caps it. Under `sm` the dialog is deliberately
        // edge-to-edge, so the cap is lifted there.
        paperFullWidth: ({ theme }) => ({
          margin: responsiveSizing.dialog.paperMargin.sm,
          width: responsiveSizing.dialog.paperWidth.sm,
          [theme.breakpoints.down('sm')]: {
            margin: responsiveSizing.dialog.paperMargin.xs,
            width: responsiveSizing.dialog.paperWidth.xs,
            maxWidth: responsiveSizing.dialog.paperMaxWidth.xs,
          },
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: responsiveSizing.dialog.titlePadding.sm,
          [theme.breakpoints.down('sm')]: {
            padding: responsiveSizing.dialog.titlePadding.xs,
          },
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: responsiveSizing.dialog.bodyPadding.sm,
          [theme.breakpoints.down('sm')]: {
            padding: responsiveSizing.dialog.bodyPadding.xs,
          },
        }),
        dividers: ({ theme }) => ({
          padding: responsiveSizing.dialog.bodyPadding.sm,
          [theme.breakpoints.down('sm')]: {
            padding: responsiveSizing.dialog.bodyPadding.xs,
          },
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: responsiveSizing.dialog.actionsPadding.sm,
          [theme.breakpoints.down('sm')]: {
            padding: responsiveSizing.dialog.actionsPadding.xs,
          },
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 12px 28px rgba(17, 59, 78, 0.08)',
        },
      },
      variants: [
        {
          props: { variant: 'modern' },
          style: {
            borderRadius: '20px',
            border: `1px solid ${modernColors.modern.slate[200]}`,
            boxShadow: '0 8px 24px rgba(17, 59, 78, 0.07)',
            background: modernColors.gradient.white,
            transition: 'all 0.26s ease',
            '&:hover': {
              borderColor: modernColors.modern.blue[300],
              boxShadow: '0 14px 30px rgba(31, 111, 139, 0.14)',
              transform: 'translateY(-2px)',
            },
          },
        },
      ],
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
      variants: [
        {
          props: { variant: 'modernInput' },
          style: {
            borderRadius: '18px',
            border: `1.6px solid ${modernColors.modern.slate[200]}`,
            boxShadow: '0 10px 24px rgba(17, 59, 78, 0.09)',
            background: modernColors.gradient.white,
            transition: 'all 0.25s ease',
            '&:hover': {
              borderColor: modernColors.modern.blue[400],
              boxShadow: '0 14px 26px rgba(31, 111, 139, 0.13)',
            },
            '&:focus-within': {
              borderColor: modernColors.modern.blue[500],
              boxShadow: '0 14px 28px rgba(31, 111, 139, 0.16)',
            },
          },
        },
        {
          props: { variant: 'modern' },
          style: {
            borderRadius: '18px',
            border: `1.6px solid ${modernColors.modern.slate[200]}`,
            boxShadow: '0 10px 24px rgba(17, 59, 78, 0.09)',
            background: modernColors.gradient.white,
            transition: 'all 0.25s ease',
            // animation: 'revealUp 650ms ease forwards',
          },
        },
      ],
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 14,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 600,
          height: responsiveSizing.controls.chipHeight,
          '& .MuiChip-label': {
            fontSize: responsiveSizing.controls.chipLabelFontSize,
          },
        },
      },
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 12,
  },
  shadows: themeShadows,
});

theme = responsiveFontSizes(theme, {
  factor: 2,
  variants: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'subtitle1', 'subtitle2', 'body1', 'body2', 'button', 'caption', 'overline'],
});

theme.modern = {
  colors: modernColors,
  typography: modernTypography,
  responsive: responsiveSizing,
  backgrounds: {
    gradient: modernColors.gradient.background,
    white: '#ffffff',
  },
  motion: {
    standard: '240ms ease',
    emphasis: '320ms cubic-bezier(0.2, 0.8, 0.2, 1)',
  },
  utils: {
    gradientText: (variant = 'primary') => {
      if (!modernColors.gradient[variant]) return {};

      return {
        background: modernColors.gradient[variant],
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      };
    },
    modernCard: {
      borderRadius: '20px',
      border: `1px solid ${modernColors.modern.slate[200]}`,
      boxShadow: '0 8px 24px rgba(17, 59, 78, 0.07)',
      background: modernColors.gradient.white,
      transition: 'all 0.26s ease',
      '&:hover': {
        borderColor: modernColors.modern.blue[300],
        boxShadow: '0 14px 30px rgba(31, 111, 139, 0.14)',
        transform: 'translateY(-2px)',
      },
    },
    modernInput: {
      borderRadius: '18px',
      border: `1.6px solid ${modernColors.modern.slate[200]}`,
      boxShadow: '0 10px 24px rgba(17, 59, 78, 0.09)',
      background: modernColors.gradient.white,
      transition: 'all 0.25s ease',
      '&:hover': {
        borderColor: modernColors.modern.blue[400],
        boxShadow: '0 14px 26px rgba(31, 111, 139, 0.13)',
      },
      '&:focus-within': {
        borderColor: modernColors.modern.blue[500],
        boxShadow: '0 14px 28px rgba(31, 111, 139, 0.16)',
      },
    },
  },
};

export default theme;